/**
 * Is this request arriving on one of *our* hostnames, or on an agency's
 * white-label portal subdomain?
 *
 * The client-portal middleware branches on this: a canonical host renders the
 * normal app, anything else is looked up as a `PortalDomain`. The asymmetry is
 * deliberate — an unknown host that we wrongly treat as canonical shows an
 * agency's client a Spyglasses login page (ugly, recoverable), while a
 * canonical host wrongly treated as a portal takes the whole app down. So the
 * empty/absent case answers "canonical", and every portal decision has to be
 * positively earned.
 *
 * Browser-safe: no node imports, no `@repo/database`.
 */

/** Hosts that are always ours, regardless of environment configuration. */
const ALWAYS_CANONICAL = new Set([
	"localhost",
	"127.0.0.1",
	"::1",
	// The shared ngrok tunnel the dev server sits behind (see CLAUDE.md).
	"spyglasses-dev.ngrok.io",
]);

/**
 * Reduce a `Host` header (or a bare hostname) to a lowercase hostname with any
 * port removed.
 *
 * Note `*.localhost` is deliberately NOT collapsed to `localhost` — portals are
 * tested locally at `acme.localhost:3015`, so that has to stay non-canonical.
 */
function normalizeHost(raw: string): string {
	const host = raw.trim().toLowerCase();
	if (!host) {
		return "";
	}

	// Bracketed IPv6, with or without a port: "[::1]:3015" -> "::1"
	if (host.startsWith("[")) {
		const end = host.indexOf("]");
		return end === -1 ? host.slice(1) : host.slice(1, end);
	}

	// Only strip a ":port" when there is exactly one colon. A bare IPv6 literal
	// ("::1") has several and carries no port, so splitting it would corrupt it.
	const firstColon = host.indexOf(":");
	if (firstColon !== -1 && host.indexOf(":", firstColon + 1) === -1) {
		return host.slice(0, firstColon);
	}

	return host;
}

/**
 * Hostname of a configured URL. Accepts both full URLs
 * ("https://app.spyglasses.io") and bare hosts ("myapp-abc.vercel.app", which
 * is how Vercel sets NEXT_PUBLIC_VERCEL_URL).
 */
function hostnameOf(value: string | undefined): string | null {
	if (!value?.trim()) {
		return null;
	}

	const trimmed = value.trim();
	try {
		const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)
			? trimmed
			: `https://${trimmed}`;
		return normalizeHost(new URL(withScheme).hostname) || null;
	} catch {
		return normalizeHost(trimmed) || null;
	}
}

/** `example.com` and `www.example.com` are the same site to us. */
function wwwVariants(host: string): string[] {
	return host.startsWith("www.")
		? [host, host.slice(4)]
		: [host, `www.${host}`];
}

/**
 * True when `host` is one of the app's own hostnames — i.e. NOT an agency
 * white-label portal domain.
 *
 * @param host - A `Host` header value, hostname, or nullish
 * @param env - Environment source; injectable so tests need not mutate process.env
 *
 * @example
 * isCanonicalHost("app.spyglasses.io")   // true  (NEXT_PUBLIC_SITE_URL)
 * isCanonicalHost("localhost:3015")      // true
 * isCanonicalHost("acme.localhost:3015") // false — a local portal under test
 * isCanonicalHost("ai.agency.com")       // false — an agency portal
 */
export function isCanonicalHost(
	host: string | null | undefined,
	env: Record<string, string | undefined> = process.env,
): boolean {
	// Absent Host header: fail toward the app rather than toward a portal.
	if (!host) {
		return true;
	}

	const normalized = normalizeHost(host);
	if (!normalized) {
		return true;
	}

	if (ALWAYS_CANONICAL.has(normalized)) {
		return true;
	}

	// Every preview/production Vercel deployment URL.
	if (normalized.endsWith(".vercel.app")) {
		return true;
	}

	const siteHost = hostnameOf(env.NEXT_PUBLIC_SITE_URL);
	if (siteHost && wwwVariants(siteHost).includes(normalized)) {
		return true;
	}

	const vercelHost = hostnameOf(env.NEXT_PUBLIC_VERCEL_URL);
	if (vercelHost && vercelHost === normalized) {
		return true;
	}

	const extra = env.NEXT_PUBLIC_CANONICAL_HOSTS;
	if (extra) {
		for (const entry of extra.split(",")) {
			const candidate = hostnameOf(entry);
			if (candidate && candidate === normalized) {
				return true;
			}
		}
	}

	return false;
}
